import "rxjs/add/operator/filter";
import "rxjs/add/observable/of";
import "rxjs/add/observable/zip";

import {Injectable} from "@angular/core";
import {IRemoteLockResult, User} from "../definitions";
import {LockService} from "./lock.service";
import {Observable} from "rxjs/Observable";
import {WatchService} from "./watch.service";
import {QueryService} from "./query.service";
import {NavigationStart, Router} from "@angular/router";
import {CollectionService} from "./collection.service";
import {BusService, INewDocumentEvent} from "./bus.service";
import {paramsHelper} from "../helpers/params-helper";
import {IDocument} from "../interfaces/document.definition";

@Injectable()
export class ProfileService {
  private _document: IDocument;
  private _readonly: boolean;
  private _user: User;

  constructor(private router: Router,
              private lockService: LockService,
              private busService: BusService,
              private collectionService: CollectionService,
              private watchService: WatchService,
              private queryService: QueryService) {
    this.router.events
      .filter(e => e instanceof NavigationStart)
      .subscribe((): void => this.reset());

    this.busService
      .onDocumentLockRemoved
      .subscribe(documentId => this.onLockRemoved(documentId));

    this.watchService.docChanged()
      .subscribe((): void => this.onDocumentUpdated());
  }


  load(documentId: string, target: string) {
    let zip = [];
    if (this.hasDocument && this.editable) {
      zip.push(this.lockService.unlock(this.document._id));
    } else {
      zip.push(Observable.of(true));
    }

    const params = paramsHelper.loader()
      .setEndpointProfiles()
      .setAdditionalParam("target", target)
      .setAdditionalParam("_id", documentId);


    zip.push(this.lockService.lock(documentId));
    zip.push(this.collectionService.loadOne(params));

    Observable.zip.apply(Observable, zip)
      .subscribe(([unlockSuccess, lockResult, loadResult]: [boolean, IRemoteLockResult, IDocument]) => {
        if (!loadResult) {
          return;
        }

        if (!lockResult.result) {
          this.setReadOnly(loadResult, lockResult.user);
          return;
        }

        this.setEditable(loadResult);
      });
  }


  reset() {
    if (this.document && this.editable) {
      this.lockService.unlock(this.document._id).subscribe();
    }
    this.clearDocument();
  }


  save() {
    // note: workaround (backend adds 'target' to profile!!!)
    const params = paramsHelper.loader()
      .setAdditionalParam("target", this.document["target"])
      .setEndpointProfiles();

    this.collectionService.saveDocument(params, this.document)
      .subscribe(():void => {
        this.emitDocumentSaved();
        this.watchService.reset();
      });
  }


  remove() {
    if (this.document && this.editable) {
      const documentId = this._document._id;
      const params = paramsHelper.loader()
        .setAdditionalParam("target", this.document["target"])
        .setAdditionalParam("_id", documentId)
        .setEndpointProfiles();

      this.collectionService.removeDocument(params)
        .subscribe((): void => {
          this.emitDocumentSaved();
          this.lockService.unlock(documentId).subscribe();
          this.clearDocument();
        });
    }
  }


  private onLockRemoved(documentId: string) {
    if (this.document
      && this.document._id === documentId
      && this.editable) {
      this.setDocumentImpl(this._document, true); // make readonly
    }
  }


  private onDocumentUpdated() {
    if (!(this.document && this.editable)) {
      return;
    }

    this.lockService.prolong(this.document._id);
  }


  private setReadOnly(document: IDocument, user: User) {
    this.setDocumentImpl(document, true, user);
  }


  private setEditable(document: IDocument) {
    this.setDocumentImpl(document, false);
  }


  private setDocumentImpl(document: IDocument, readonly: boolean, user: User = null) {
    this._document = document;
    this._readonly = readonly;
    this._user = user;

    this.watchService.reset();
    this.queryService.update(document);
    this.emitDocumentUpdated();
  }


  clearDocument() {
    this._readonly = false;
    this._document = null;

    this.watchService.reset();
    this.queryService.reset();
    this.emitDocumentUpdated();
  }


  private emitDocumentUpdated() {
    this.busService.emitDocumentUpdated(this.createDocumentUpdatedEvent());
  }


  private emitDocumentSaved() {
    this.busService.emitDocumentSaved();
  }


  createDocumentUpdatedEvent(): INewDocumentEvent {
    return {
      document: this.document,
      readonly: this.readonly,
      user: this._user ? this._user : <any>{displayName: "system"}
    };
  }


  get readonly(): boolean {
    return this._readonly;
  }


  get editable(): boolean {
    return !this.readonly;
  }

  get hasDocument(): boolean {
    return !!this._document;
  }

  get document(): IDocument {
    return this._document;
  }
}



// WEBPACK FOOTER //
// /opt/app/develop/src/app/services/profile.service.ts